/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * ContextMenu — Premium Right-Click Menu (Radix)
 * ============================================================================
 *
 * Replaces browser context menu with themed custom menu.
 * Features: spring entrance, staggered item animation, brand-tinted icons
 * on highlight, deep shadow, backdrop blur, prefers-reduced-motion.
 *
 * ============================================================================
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import * as ContextMenuPrimitive from '@radix-ui/react-context-menu';
import { useTranslation } from 'react-i18next';
import {
  Copy,
  Scissors,
  ClipboardPaste,
  TextSelect,
  MousePointerClick,
} from 'lucide-react';

function checkEditable(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input') {
    const type = (el as HTMLInputElement).type.toLowerCase();
    return ['text', 'search', 'url', 'tel', 'email', 'password', 'number'].includes(type);
  }
  if (tag === 'textarea') return true;
  if (el.isContentEditable) return true;
  return false;
}

interface ContextMenuRootProps {
  children: ReactNode;
}

export function ContextMenuRoot({ children }: ContextMenuRootProps) {
  const { t } = useTranslation('common');
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const handleCut = useCallback(() => { document.execCommand('cut'); }, []);

  const handleCopy = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString() || '';
    if (text && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      document.execCommand('copy');
    }
  }, []);

  const handlePaste = useCallback(async () => {
    try {
      if (navigator.clipboard && targetElement) {
        const text = await navigator.clipboard.readText();
        if (targetElement.tagName === 'INPUT' || targetElement.tagName === 'TEXTAREA') {
          const input = targetElement as HTMLInputElement | HTMLTextAreaElement;
          const start = input.selectionStart ?? input.value.length;
          const end = input.selectionEnd ?? input.value.length;
          const before = input.value.substring(0, start);
          const after = input.value.substring(end);
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
          )?.set || Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype, 'value'
          )?.set;
          nativeInputValueSetter?.call(input, before + text + after);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          const newPos = start + text.length;
          input.setSelectionRange(newPos, newPos);
        } else {
          document.execCommand('insertText', false, text);
        }
      } else {
        document.execCommand('paste');
      }
    } catch {
      document.execCommand('paste');
    }
  }, [targetElement]);

  const handleSelectAll = useCallback(() => {
    if (isEditable && targetElement) {
      const tag = targetElement.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea') {
        (targetElement as HTMLInputElement).select();
      } else {
        document.execCommand('selectAll');
      }
    } else {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(document.body);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditable, targetElement]);

  const handleSelectWord = useCallback(() => {
    const selection = window.getSelection();
    if (selection && targetElement) {
      selection.modify('move', 'backward', 'word');
      selection.modify('extend', 'forward', 'word');
    }
  }, [targetElement]);

  return (
    <>
      <style>{cmStyles}</style>
      <ContextMenuPrimitive.Root>
        <ContextMenuPrimitive.Trigger asChild>
          <div
            style={{ display: 'contents' }}
            onContextMenu={(e) => {
              const target = e.target as HTMLElement;
              if (target.tagName === 'VIDEO' || target.tagName === 'CANVAS') return;
              setTargetElement(target);
              setHasSelection(!!window.getSelection()?.toString().trim());
              setIsEditable(checkEditable(target));
            }}
          >
            {children}
          </div>
        </ContextMenuPrimitive.Trigger>

        <ContextMenuPrimitive.Portal>
          <ContextMenuPrimitive.Content className="cm-menu">
            {isEditable && (
              <ContextMenuPrimitive.Item
                className="cm-item"
                disabled={!hasSelection}
                onSelect={handleCut}
              >
                <span className="cm-icon"><Scissors size={14} /></span>
                <span className="cm-label">{t('contextMenu.cut', 'Cut')}</span>
                <span className="cm-shortcut">Ctrl+X</span>
              </ContextMenuPrimitive.Item>
            )}

            <ContextMenuPrimitive.Item
              className="cm-item"
              disabled={!hasSelection}
              onSelect={handleCopy}
            >
              <span className="cm-icon"><Copy size={14} /></span>
              <span className="cm-label">{t('contextMenu.copy', 'Copy')}</span>
              <span className="cm-shortcut">Ctrl+C</span>
            </ContextMenuPrimitive.Item>

            {isEditable && (
              <ContextMenuPrimitive.Item className="cm-item" onSelect={handlePaste}>
                <span className="cm-icon"><ClipboardPaste size={14} /></span>
                <span className="cm-label">{t('contextMenu.paste', 'Paste')}</span>
                <span className="cm-shortcut">Ctrl+V</span>
              </ContextMenuPrimitive.Item>
            )}

            <ContextMenuPrimitive.Separator className="cm-sep" />

            <ContextMenuPrimitive.Item className="cm-item" onSelect={handleSelectAll}>
              <span className="cm-icon"><TextSelect size={14} /></span>
              <span className="cm-label">{t('contextMenu.selectAll', 'Select All')}</span>
              <span className="cm-shortcut">Ctrl+A</span>
            </ContextMenuPrimitive.Item>

            {!isEditable && (
              <ContextMenuPrimitive.Item
                className="cm-item"
                disabled={hasSelection}
                onSelect={handleSelectWord}
              >
                <span className="cm-icon"><MousePointerClick size={14} /></span>
                <span className="cm-label">{t('contextMenu.selectWord', 'Select Word')}</span>
              </ContextMenuPrimitive.Item>
            )}
          </ContextMenuPrimitive.Content>
        </ContextMenuPrimitive.Portal>
      </ContextMenuPrimitive.Root>
    </>
  );
}

const cmStyles = `
  /* ── Menu ── */
  .cm-menu {
    z-index: 9999;
    min-width: 200px;
    max-width: 260px;
    background: var(--color-bg-secondary);
    border: 1.5px solid var(--color-border-default, rgba(255,255,255,0.08));
    border-radius: 3px;
    box-shadow:
      0 12px 40px rgba(0,0,0,0.35),
      0 4px 12px rgba(0,0,0,0.15);
    padding: 4px;
    outline: none;
    user-select: none;
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    animation: cm-in 180ms cubic-bezier(0.16,1,0.3,1);
    overflow: hidden;
  }

  [data-theme="light"] .cm-menu {
    background: rgba(255,255,255,0.96);
    border-color: #E2E8F0;
    box-shadow:
      0 12px 40px rgba(0,0,0,0.12),
      0 4px 12px rgba(0,0,0,0.06);
  }

  @keyframes cm-in {
    from { opacity: 0; transform: scale(0.95) translateY(-4px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  /* ── Item ── */
  .cm-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    background: none;
    color: var(--color-text-primary);
    font-family: var(--font-body);
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    border-radius: 3px;
    outline: none;
    user-select: none;
    transition: background 120ms ease, transform 120ms ease;
  }

  .cm-item[data-highlighted] {
    background: var(--color-bg-hover);
    transform: translateX(2px);
  }

  .cm-item[data-disabled] {
    opacity: 0.3;
    cursor: default;
    transform: none !important;
  }

  /* ── Icon ── */
  .cm-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 3px;
    flex-shrink: 0;
    color: var(--color-text-muted);
    background: transparent;
    transition: background 120ms ease, color 120ms ease;
  }

  .cm-item[data-highlighted] .cm-icon {
    background: var(--color-brand-glow, rgba(139,0,0,0.12));
    color: var(--color-brand-primary);
  }

  [data-theme="light"] .cm-item[data-highlighted] .cm-icon {
    background: rgba(26,95,122,0.1);
    color: var(--color-brand-primary);
  }

  .cm-label { flex: 1; }

  .cm-shortcut {
    font-size: 11px;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
    margin-left: auto;
    padding-left: 16px;
    opacity: 0.7;
  }

  /* ── Separator ── */
  .cm-sep {
    height: 1px;
    margin: 4px 8px;
    background: var(--color-border-default, rgba(255,255,255,0.06));
  }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .cm-menu,
    .cm-item {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }
`;

// Backward-compatible named export
export function ContextMenu() {
  return null;
}

export default ContextMenu;
